import { Link, useLocation } from 'react-router-dom';
import {
  Activity,
  Building,
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  CalendarRange,
  LayoutDashboard,
  Library,
  ListVideo,
  LucideIcon,
  MonitorPlay,
  Palette,
  PanelsTopLeft,
  Settings,
  Users,
} from 'lucide-react';

import { cn } from '@/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { buttonVariants } from '@/components/ui/button';
import { VenueDropDownMenu } from '../common/VenueDropDownMenu';
import { LogoutButton } from '../../features/auth/components/LogoutButton';
import { ThemeSwitcher } from '../common/ThemeSwitcher';
import { SignyLogo } from '../SignyLogo';
import { SignyIcon } from '../SignyIcon';
import { useMe } from '@/features/auth/api/getUser';
import { useClientStore } from '@/stores/clientStore';

interface SidebarProps {
  isCollapsed: boolean;
  mobile?: boolean;
  onNavigate?: () => void;
}

type RouteVariant = 'ghost' | 'link' | 'default' | 'destructive' | 'outline' | 'secondary';

type RouteType = {
  title: string;
  path: string;
  icon: LucideIcon;
  variant: RouteVariant;
  adminOnly: boolean;
  requiresVenue?: boolean;
};

const routes: RouteType[] = [
  {
    title: 'Dashboard',
    path: 'dashboard',
    icon: LayoutDashboard,
    variant: 'ghost',
    adminOnly: false,
  },
  {
    title: 'Sidor',
    path: 'pages',
    icon: PanelsTopLeft,
    variant: 'ghost',
    adminOnly: false,
  },
  {
    title: 'Spellistor',
    path: 'playlists',
    icon: ListVideo,
    variant: 'ghost',
    adminOnly: false,
  },
  {
    title: 'Media',
    path: 'media-library',
    icon: Library,
    variant: 'ghost',
    adminOnly: false,
  },
  {
    title: 'Skärmar',
    path: 'displays',
    icon: MonitorPlay,
    variant: 'ghost',
    adminOnly: false,
  },
  {
    title: 'Teman',
    path: 'themes',
    icon: Palette,
    variant: 'ghost',
    adminOnly: false,
  },
  {
    title: 'Schemaläggning',
    path: 'schedules',
    icon: CalendarClock,
    variant: 'ghost',
    adminOnly: false,
  },
  {
    title: 'Anläggning',
    path: 'venue-settings',
    icon: Settings,
    variant: 'ghost',
    adminOnly: false,
    requiresVenue: true,
  },
  {
    title: 'Användare',
    path: 'users',
    icon: Users,
    variant: 'ghost',
    adminOnly: false,
  },
  {
    title: 'Bokningsbara enheter',
    path: 'bookables',
    icon: CalendarPlus,
    variant: 'ghost',
    adminOnly: false,
  },
  {
    title: 'Bokningsöversikt',
    path: 'booking-items',
    icon: CalendarRange,
    variant: 'ghost',
    adminOnly: false,
  },
  {
    title: 'Bokningar',
    path: 'bookings',
    icon: CalendarDays,
    variant: 'ghost',
    adminOnly: false,
  },
  {
    title: 'Övervakning',
    path: 'monitoring',
    icon: Activity,
    variant: 'ghost',
    adminOnly: true,
  },
  {
    title: 'Klienter',
    path: 'clients',
    icon: Building,
    variant: 'ghost',
    adminOnly: true,
  },
] as const;

export function Sidebar({ isCollapsed = true, mobile = false, onNavigate }: SidebarProps) {
  const { pathname } = useLocation();
  const venueId = useClientStore(state => state.venueId);
  const { data: user } = useMe({ venueId: venueId });

  return (
    <div
      className={cn(
        'h-screen border-r border-input bg-card',
        mobile ? 'block md:hidden w-full' : 'hidden md:block',
        !mobile && (isCollapsed ? 'min-w-[50px] transition-all duration-300 ease-in-out' : 'w-[220px]')
      )}
    >
      <div data-collapsed={isCollapsed} className="group flex h-full flex-col justify-between gap-4 overflow-x-hidden overflow-y-auto data-[collapsed=true]:py-2">
        <nav className="grid gap-1 px-2 group-[[data-collapsed=true]]:justify-center group-[[data-collapsed=true]]:px-2 py-2">
          {isCollapsed ? (
            <div className="w-[40px] flex justify-center mt-2 mb-6">
              <SignyIcon />
            </div>
          ) : (
            <div className="w-32 mt-4">
              <SignyLogo />
            </div>
          )}

          {!isCollapsed && (
            <div className="w-full mb-4 mt-6">
              <VenueDropDownMenu />
            </div>
          )}

          {routes
            .filter(route => (['bookables', 'bookings', 'booking-items'].includes(route.path) && !user?.hasBookingAccess && !user?.isAdmin ? false : true))
            .filter(route => !route.requiresVenue || !!venueId)
            .filter(route => (route.adminOnly && user?.isAdmin) || !route.adminOnly)
            .map((link, index) => {
              const to = link.requiresVenue && venueId ? `/venue/${venueId}` : link.path;
              const isActive = link.requiresVenue ? pathname.startsWith('/venue/') : pathname.includes(link.path);

              return isCollapsed ? (
                <Tooltip key={index} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Link
                      to={to}
                      onClick={onNavigate}
                      className={cn(
                        buttonVariants({ variant: link.variant, size: 'icon' }),
                        'h-9 w-9',
                        isActive && 'bg-brand text-[white_!important] transition-[background] duration-1000',
                        link.variant === 'default' && 'dark:bg-muted dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-white'
                      )}
                    >
                      <link.icon className="h-4 w-4" />
                      <span className="sr-only">{link.title}</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="flex items-center gap-4">
                    {link.title}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Link
                  key={index}
                  to={to}
                  onClick={onNavigate}
                  className={cn(
                    buttonVariants({ variant: link.variant, size: 'sm' }),
                    link.variant === 'default' && 'dark:bg-muted dark:text-white dark:hover:bg-muted dark:hover:text-white',
                    isActive && 'bg-muted',
                    'justify-start relative'
                  )}
                >
                  <link.icon className="mr-2 h-4 w-4" />
                  {link.title}
                </Link>
              );
            })}
        </nav>

        <nav className="flex flex-col justify-end py-2">
          <div className="p-2">
            <ThemeSwitcher isCollapsed={isCollapsed} />
          </div>
          <LogoutButton isCollapsed={isCollapsed} />
        </nav>
      </div>
    </div>
  );
}
